import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Card,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  Grid,
  Icon,
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  TablePagination,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TextField,
} from "@mui/material";
import {
  Add,
  CalendarToday,
  Close,
  Edit,
  FilterList,
  Inventory2Outlined,
  Search,
  ReceiptLong,
  WarningAmber,
		Delete,
  ExpandMore,
} from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { useTheme } from "@mui/material/styles";
import dayjs from "dayjs";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDBox from "components/MDBox";
import ApiCalls from "services/flick_erp_api_call";
import colors from "assets/theme/base/colors";
import rgba from "assets/theme/functions/rgba";
import { useMaterialUIController } from "context";

const statusOptions = ["in_stock", "assigned", "not_working", "sold"];

const defaultRecord = {
  brand: "",
  name: "",
  serial_number: "",
  purchase_date: "",
  purchased_from: "",
  status: "in_stock",
  warranty_till: "",
  invoice: null,
  remarks: "",
};

const formatDate = (value) => {
  if (!value) return "N/A";
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format("DD MMM YYYY") : "N/A";
};

const formatStatus = (value) =>
  (value || "unknown")
    .split("_")
    .map((item) => item.charAt(0).toUpperCase() + item.slice(1))
    .join(" ");

const getStatusColor = (status) => {
  switch (status) {
    case "in_stock":
      return "success";
    case "assigned":
      return "info";
    case "not_working":
      return "warning";
    case "sold":
      return "default";
    default:
      return "default";
  }
};

const Inventory = () => {
  const theme = useTheme();
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const palette = theme.palette;

  const ui = {
    card: darkMode ? palette.background.card : colors.white.main,
    soft: darkMode ? palette.background.muted : "#f8fafc",
    border: darkMode ? rgba(palette.white.main, 0.08) : rgba(colors.secondary.main, 0.12),
    title: darkMode ? palette.text.focus : colors.dark.main,
    body: darkMode ? palette.text.main : colors.text.main,
    muted: darkMode ? rgba(palette.text.main, 0.72) : rgba(colors.text.main, 0.8),
    shadow: darkMode ? "0 18px 40px rgba(2, 6, 23, 0.28)" : `0 18px 40px ${rgba(colors.dark.main, 0.06)}`,
  };

  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [openDrawer, setOpenDrawer] = useState(false);
  const [openDetailsDrawer, setOpenDetailsDrawer] = useState(false);
  const [drawerMode, setDrawerMode] = useState("add");
  const [selectedRecord, setSelectedRecord] = useState(defaultRecord);
  const [selectedViewRecord, setSelectedViewRecord] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(0);
  const rowsPerPage = 10;

  const loadInventory = async () => {
    setLoading(true);
    try {
      const res = await ApiCalls.getHardwareItems();
      setRecords(Array.isArray(res?.data) ? res.data : []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadInventory();
  }, []);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const handleStatusFilterChange = (event) => {
    setStatusFilter(event.target.value);
    setPage(0);
  };

  const openAddDrawer = () => {
    setDrawerMode("add");
    setSelectedRecord(defaultRecord);
    setOpenDrawer(true);
  };

  const openEditDrawer = (record) => {
    setDrawerMode("edit");
    setSelectedRecord({
      id: record.id,
      brand: record.brand || "",
      name: record.name || "",
      serial_number: record.serial_number || "",
      purchase_date: record.purchase_date || "",
      purchased_from: record.purchased_from || "",
      status: record.status || "in_stock",
      warranty_till: record.warranty_till || "",
      invoice: record.invoice || null,
      remarks: record.remarks || "",
    });
    setOpenDrawer(true);
  };

  const openRowDetails = (record) => {
    setSelectedViewRecord(record);
    setOpenDetailsDrawer(true);
  };

  const handleDrawerInputChange = (field, value) => {
    setSelectedRecord((prev) => ({ ...prev, [field]: value }));
  };

  const buildFormData = (record) => {
    const formData = new FormData();
    formData.append("type", record.brand || "");
    formData.append("name", record.name || "");
    formData.append("serial_number", record.serial_number || "");
    formData.append("status", record.status || "in_stock");
    formData.append("warranty_till", record.warranty_till || "");
    formData.append("purchase_date", record.purchase_date || "");
    formData.append("purchased_from", record.purchased_from || "");
    formData.append("brand", record.brand || "");
    formData.append("remarks", record.remarks || "");
    formData.append("configuration", JSON.stringify({}));

    if (record.invoice instanceof File) {
      formData.append("invoice", record.invoice);
    }

    return formData;
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (drawerMode === "add") {
        const res = await ApiCalls.postHardwareItems(buildFormData(selectedRecord));
        if (res?.code === 201) {
          setOpenDrawer(false);
          await loadInventory();
        }
      } else {
        const res = await ApiCalls.patchHardwareItems(selectedRecord.id, buildFormData(selectedRecord));
        if (res?.success || res?.code === 200) {
          setOpenDrawer(false);
          await loadInventory();
        }
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    const res = await ApiCalls.deleteHardwareItems(id);
    if (res?.code === 204) {
      await loadInventory();
    }
  };

  const filteredRecords = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    return records.filter((record) => {
      const matchesStatus = statusFilter === "all" ? true : record.status === statusFilter;

      if (!matchesStatus) return false;
      if (!normalizedSearch) return true;

      const searchableValues = [
        record?.id,
        record?.brand,
        record?.name,
        record?.serial_number,
      ]
        .filter(Boolean)
        .map((value) => String(value).toLowerCase());

      return searchableValues.some((value) => value.includes(normalizedSearch));
    });
  }, [records, searchTerm, statusFilter]);

  const paginatedRecords = useMemo(
    () => filteredRecords.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filteredRecords, page]
  );

  const summary = useMemo(() => {
    const inStock = records.filter((item) => item.status === "in_stock").length;
    const assigned = records.filter((item) => item.status === "assigned").length;
    const notWorking = records.filter((item) => item.status === "not_working").length;
    const expiringSoon = records.filter((item) => {
      if (!item.warranty_till) return false;
      const diff = dayjs(item.warranty_till).diff(dayjs(), "day");
      return diff >= 0 && diff <= 30;
    }).length;

    return {
      total: records.length,
      inStock,
      assigned,
      notWorking,
      expiringSoon,
    };
  }, [records]);

  const StatCard = ({ icon, label, value, subtitle }) => (
    <Card
      sx={{
        p: 2,
        borderRadius: "20px",
        boxShadow: "none",
        border: `1px solid ${ui.border}`,
        background: ui.soft,
        height: "100%",
      }}
    >
      <Stack direction="row" spacing={4} justifyContent="space-between" alignItems="center">
        <Box
          sx={{
            width: 42,
            height: 42,
            borderRadius: "14px",
            display: "grid",
            placeItems: "center",
            background: `linear-gradient(135deg, ${colors.primary.main} 0%, ${colors.info.main} 100%)`,
            color: colors.white.main,
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>
        <Box>
          <MDTypography variant="button" fontWeight="medium" sx={{ color: ui.muted, display : "flex", justifyContent : "flex-end" }}>
            {label}
          </MDTypography>
          <MDTypography variant="h5" fontWeight="bold" sx={{ color: ui.title, lineHeight: 1.2, display : "flex", justifyContent : "flex-end" }}>
            {value}
          </MDTypography>
          <MDTypography variant="caption" sx={{ color: ui.muted, display : "flex", justifyContent : "flex-end" }}>
            {subtitle}
          </MDTypography>
        </Box>
      </Stack>
    </Card>
  );

  return (
    <Card
      sx={{
        p: 2,
        borderRadius: "28px",
        border: `1px solid ${ui.border}`,
        backgroundColor: ui.card,
        boxShadow: ui.shadow,
      }}
    >
      <MDBox display="flex" justifyContent="space-between" alignItems="center" gap={2} flexWrap="wrap" mb={2}>
        <MDBox>
          <MDTypography variant="h4" sx={{ color: ui.title }}>
            Inventory
          </MDTypography>
          <MDTypography variant="body2" sx={{ color: ui.muted, mt: 0.5 }}>
            Manage laptops, peripherals, warranty details, and uploaded invoices in one place.
          </MDTypography>
        </MDBox>
        <MDButton
          size="small"
          variant="contained"
          color="info"
          startIcon={<Add />}
          sx={{ borderRadius: "14px" }}
          onClick={openAddDrawer}
        >
          Add Asset
        </MDButton>
      </MDBox>

      <Grid container spacing={2} mb={2}>
        <Grid item xs={12} md={3}>
          <StatCard
            icon={<Inventory2Outlined fontSize="small" />}
            label="Total Assets"
            value={summary.total}
            subtitle="All inventory records"
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <StatCard
            icon={<ReceiptLong fontSize="small" />}
            label="In Stock"
            value={summary.inStock}
            subtitle="Ready to assign"
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <StatCard
            icon={<Edit fontSize="small" />}
            label="Assigned"
            value={summary.assigned}
            subtitle="Assigned to employees"
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <StatCard
            icon={<WarningAmber fontSize="small" />}
            label="Warranty Expiring"
            value={summary.expiringSoon}
            subtitle="Within the next 30 days"
          />
        </Grid>
      </Grid>

      <Card
        sx={{
          p: 2,
          mb: 2,
          borderRadius: "20px",
          boxShadow: "none",
          border: `1px solid ${ui.border}`,
          background: ui.soft,
        }}
      >
        <Grid container spacing={2}>
          <Grid item xs={12} md={8}>
            <MDTypography variant="body2" fontWeight="bold" mb={0.75} sx={{ color: ui.title }}>
              Search Inventory
            </MDTypography>
            <TextField
              fullWidth
              value={searchTerm}
              onChange={handleSearchChange}
              placeholder="Search by asset ID, category, asset name, or serial number"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ color: ui.muted }} fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <MDTypography variant="body2" fontWeight="bold" mb={0.75} sx={{ color: ui.title }}>
              Filter By Status
            </MDTypography>
            <TextField
              select
              fullWidth
              value={statusFilter}
              onChange={handleStatusFilterChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start" sx={{padding : '20px'}}>
                    <FilterList sx={{ color: ui.muted }} fontSize="small" />
                  </InputAdornment>
                ),
              }}
            >
              <MenuItem value="all">All</MenuItem>
              {statusOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  {formatStatus(option)}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
      </Card>

      <Drawer
        anchor="right"
        open={openDrawer}
        onClose={() => setOpenDrawer(false)}
        PaperProps={{
          sx: {
            width: 390,
            margin: "0 !important",
            height: "100%",
            borderRadius: 0,
            boxShadow: 3,
            bgcolor: ui.soft,
            borderLeft: `1px solid ${ui.border}`,
          },
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center" m={2}>
          <Box>
            <MDTypography variant="h6" fontWeight="bold" sx={{ color: ui.title }}>
              {drawerMode === "add" ? "Add Asset" : "Edit Asset"}
            </MDTypography>
            <MDTypography variant="body2" sx={{ color: ui.muted }}>
              {drawerMode === "add"
                ? "Create a new inventory record for HR and admin teams."
                : "Update the selected inventory record."}
            </MDTypography>
          </Box>
          <IconButton onClick={() => setOpenDrawer(false)}>
            <Close />
          </IconButton>
        </Box>
        <Divider sx={{ mb: 2 }} />

        <Box sx={{ width: 390, maxWidth: "100%", p: 3, display: "flex", flexDirection: "column", gap: 2 }}>
          <Box>
            <MDTypography variant="body2" fontWeight="bold" mb={0.5} sx={{ color: ui.title }}>
              Category / Brand
            </MDTypography>
            <TextField
              variant="outlined"
              value={selectedRecord.brand || ""}
              onChange={(e) => handleDrawerInputChange("brand", e.target.value)}
              placeholder="Apple, Lenovo, Mouse, Headphones..."
              fullWidth
            />
          </Box>

          <Box>
            <MDTypography variant="body2" fontWeight="bold" mb={0.5} sx={{ color: ui.title }}>
              Asset Name
            </MDTypography>
            <TextField
              variant="outlined"
              value={selectedRecord.name || ""}
              onChange={(e) => handleDrawerInputChange("name", e.target.value)}
              placeholder="MacBook Air, Dell 5420, Logitech H151..."
              fullWidth
            />
          </Box>

          <Box>
            <MDTypography variant="body2" fontWeight="bold" mb={0.5} sx={{ color: ui.title }}>
              Serial Number / Asset ID
            </MDTypography>
            <TextField
              variant="outlined"
              value={selectedRecord.serial_number || ""}
              onChange={(e) => handleDrawerInputChange("serial_number", e.target.value)}
              placeholder="Enter unique serial number"
              fullWidth
            />
          </Box>

          <Box>
            <MDTypography variant="body2" fontWeight="bold" mb={0.5} sx={{ color: ui.title }}>
              Purchase Date
            </MDTypography>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                value={selectedRecord.purchase_date ? dayjs(selectedRecord.purchase_date) : null}
                onChange={(newVal) =>
                  handleDrawerInputChange("purchase_date", newVal ? newVal.format("YYYY-MM-DD") : "")
                }
                format="YYYY-MM-DD"
                slotProps={{
                  textField: {
                    variant: "outlined",
                    placeholder: "YYYY-MM-DD",
                    fullWidth: true,
                  },
                }}
              />
            </LocalizationProvider>
          </Box>

          <Box>
            <MDTypography variant="body2" fontWeight="bold" mb={0.5} sx={{ color: ui.title }}>
              Purchased From
            </MDTypography>
            <TextField
              variant="outlined"
              value={selectedRecord.purchased_from || ""}
              onChange={(e) => handleDrawerInputChange("purchased_from", e.target.value)}
              placeholder="Amazon, Apple Store, Local Vendor..."
              fullWidth
            />
          </Box>

          <Box>
            <MDTypography variant="body2" fontWeight="bold" mb={0.5} sx={{ color: ui.title }}>
              Status
            </MDTypography>
            <TextField
              select
              variant="outlined"
              value={selectedRecord.status || "in_stock"}
              onChange={(e) => handleDrawerInputChange("status", e.target.value)}
              fullWidth
            >
              {statusOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  {formatStatus(option)}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          <Box>
            <MDTypography variant="body2" fontWeight="bold" mb={0.5} sx={{ color: ui.title }}>
              Warranty Till
            </MDTypography>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                value={selectedRecord.warranty_till ? dayjs(selectedRecord.warranty_till) : null}
                onChange={(newVal) =>
                  handleDrawerInputChange("warranty_till", newVal ? newVal.format("YYYY-MM-DD") : "")
                }
                format="YYYY-MM-DD"
                slotProps={{
                  textField: {
                    variant: "outlined",
                    placeholder: "YYYY-MM-DD",
                    fullWidth: true,
                  },
                }}
              />
            </LocalizationProvider>
          </Box>

          <Box>
            <MDTypography variant="body2" fontWeight="bold" mb={0.5} sx={{ color: ui.title }}>
              Remarks
            </MDTypography>
            <TextField
              variant="outlined"
              value={selectedRecord.remarks || ""}
              onChange={(e) => handleDrawerInputChange("remarks", e.target.value)}
              placeholder="Optional notes about condition, ownership, or history"
              fullWidth
              multiline
              minRows={3}
            />
          </Box>

          <Box>
            <MDTypography variant="body2" fontWeight="bold" mb={0.5} sx={{ color: ui.title }}>
              Upload Invoice
            </MDTypography>
            <input
              type="file"
              onChange={(e) => handleDrawerInputChange("invoice", e.target.files?.[0] || null)}
            />
            {drawerMode === "edit" && typeof selectedRecord.invoice === "string" ? (
              <MDTypography variant="caption" sx={{ display: "block", mt: 0.75, color: ui.muted }}>
                Current invoice is already uploaded.
              </MDTypography>
            ) : null}
          </Box>

          <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
            <MDButton
              size="small"
              variant="contained"
              color="info"
              onClick={handleSave}
              disabled={!selectedRecord.name || !selectedRecord.brand || saving}
            >
              {saving ? "Saving..." : drawerMode === "add" ? "Save Asset" : "Update Asset"}
            </MDButton>
            <MDButton size="small" variant="outlined" color="dark" onClick={() => setOpenDrawer(false)}>
              Cancel
            </MDButton>
          </Box>
        </Box>
      </Drawer>

      <Drawer
        anchor="right"
        open={openDetailsDrawer}
        onClose={() => setOpenDetailsDrawer(false)}
        PaperProps={{
          sx: {
            width: 420,
            margin: "0 !important",
            height: "100%",
            borderRadius: 0,
            boxShadow: 3,
            bgcolor: ui.soft,
            borderLeft: `1px solid ${ui.border}`,
          },
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center" m={2}>
          <Box>
            <MDTypography variant="h6" fontWeight="bold" sx={{ color: ui.title }}>
              Asset Details
            </MDTypography>
            <MDTypography variant="body2" sx={{ color: ui.muted }}>
              View details and assignment history.
            </MDTypography>
          </Box>
          <IconButton onClick={() => setOpenDetailsDrawer(false)}>
            <Close />
          </IconButton>
        </Box>
        <Divider sx={{ mb: 2 }} />

        <Box sx={{ p: 2.5, pt: 0.5 }}>
          <Card
            sx={{
              p: 2,
              borderRadius: "18px",
              boxShadow: "none",
              border: `1px solid ${ui.border}`,
              background: ui.card,
            }}
          >
            <MDTypography variant="h6" sx={{ color: ui.title }}>
              {selectedViewRecord?.name || "Unnamed asset"}
            </MDTypography>
            <MDTypography variant="body2" sx={{ color: ui.muted, mt: 0.25 }}>
              {selectedViewRecord?.brand || "N/A"} • {selectedViewRecord?.serial_number || "No serial"}
            </MDTypography>

            <Stack spacing={0.8} mt={1.5}>
              <MDTypography variant="body2" sx={{ color: ui.body }}>
                ID: <strong>{selectedViewRecord?.id ?? "N/A"}</strong>
              </MDTypography>
              <MDTypography variant="body2" sx={{ color: ui.body }}>
                Status: <strong>{formatStatus(selectedViewRecord?.status)}</strong>
              </MDTypography>
              <MDTypography variant="body2" sx={{ color: ui.body }}>
                Purchase date: <strong>{formatDate(selectedViewRecord?.purchase_date)}</strong>
              </MDTypography>
              <MDTypography variant="body2" sx={{ color: ui.body }}>
                Purchased from: <strong>{selectedViewRecord?.purchased_from || "N/A"}</strong>
              </MDTypography>
              <MDTypography variant="body2" sx={{ color: ui.body }}>
                Warranty till: <strong>{formatDate(selectedViewRecord?.warranty_till)}</strong>
              </MDTypography>
              {selectedViewRecord?.invoice ? (
                <MDTypography variant="body2" sx={{ color: ui.body }}>
                  Invoice:{" "}
                  <a href={selectedViewRecord.invoice} target="_blank" rel="noreferrer">
                    View Invoice
                  </a>
                </MDTypography>
              ) : null}
              {selectedViewRecord?.remarks ? (
                <MDTypography variant="body2" sx={{ color: ui.body }}>
                  Remarks: <strong>{selectedViewRecord.remarks}</strong>
                </MDTypography>
              ) : null}
            </Stack>
          </Card>

          <Box mt={2}>
            <Accordion
              defaultExpanded
              disableGutters
              elevation={0}
              sx={{
                borderRadius: "18px",
                border: `1px solid ${ui.border}`,
                overflow: "hidden",
                "&:before": { display: "none" },
                background: ui.card,
              }}
            >
              <AccordionSummary expandIcon={<ExpandMore />} sx={{ px: 2 }}>
                <MDTypography variant="button" fontWeight="medium" sx={{ color: ui.title }}>
                  Assignments ({selectedViewRecord?.assignments?.length || 0})
                </MDTypography>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 2, pb: 2 }}>
                {(selectedViewRecord?.assignments || []).length === 0 ? (
                  <MDTypography variant="body2" sx={{ color: ui.muted }}>
                    No assignment history.
                  </MDTypography>
                ) : (
                  <Stack spacing={1.25}>
                    {(selectedViewRecord.assignments || []).map((a) => (
                      <Card
                        key={a.id}
                        sx={{
                          p: 1.5,
                          borderRadius: "14px",
                          boxShadow: "none",
                          border: `1px solid ${ui.border}`,
                          background: ui.soft,
                        }}
                      >
                        <Stack direction="row" justifyContent="space-between" spacing={1}>
                          <Box>
                            <MDTypography variant="button" fontWeight="medium" sx={{ color: ui.title }}>
                              Employee ID: {a.employee ?? "N/A"}
                            </MDTypography>
                            <MDTypography variant="caption" sx={{ color: ui.muted, display: "block", mt: 0.25 }}>
                              Assigned: {a.assigned_date || "N/A"}
                            </MDTypography>
                            <MDTypography variant="caption" sx={{ color: ui.muted, display: "block" }}>
                              Unassigned: {a.unassigned_date || "Active"}
                            </MDTypography>
                            {a.unassigned_reason ? (
                              <MDTypography variant="caption" sx={{ color: ui.muted, display: "block" }}>
                                Reason: {a.unassigned_reason}
                              </MDTypography>
                            ) : null}
                          </Box>
                          <Chip
                            size="small"
                            label={a.unassigned_date ? "Past" : "Active"}
                            color={a.unassigned_date ? "default" : "success"}
                            variant="outlined"
                          />
                        </Stack>
                      </Card>
                    ))}
                  </Stack>
                )}
              </AccordionDetails>
            </Accordion>
          </Box>

          <Stack direction="row" spacing={1} mt={2} justifyContent="flex-end">
            {selectedViewRecord ? (
              <>
                <MDButton
                  size="small"
                  variant="outlined"
                  color="info"
                  onClick={() => {
                    setOpenDetailsDrawer(false);
                    openEditDrawer(selectedViewRecord);
                  }}
                >
                  Edit
                </MDButton>
                <MDButton
                  size="small"
                  variant="outlined"
                  color="error"
                  onClick={() => handleDelete(selectedViewRecord.id)}
                >
                  Delete
                </MDButton>
              </>
            ) : null}
          </Stack>
        </Box>
      </Drawer>

      {loading ? (
        <Box sx={{ py: 8, display: "grid", placeItems: "center" }}>
          <CircularProgress size={30} />
        </Box>
      ) : records.length === 0 ? (
        <Card
          sx={{
            p: 4,
            borderRadius: "20px",
            boxShadow: "none",
            border: `1px dashed ${ui.border}`,
            background: ui.soft,
            textAlign: "center",
          }}
        >
          <MDTypography variant="h6" sx={{ color: ui.title }}>
            No inventory records yet
          </MDTypography>
          <MDTypography variant="body2" sx={{ color: ui.muted, mt: 0.5 }}>
            Add your first asset to start tracking devices, warranty details, and invoices.
          </MDTypography>
        </Card>
      ) : filteredRecords.length === 0 ? (
        <Card
          sx={{
            p: 4,
            borderRadius: "20px",
            boxShadow: "none",
            border: `1px dashed ${ui.border}`,
            background: ui.soft,
            textAlign: "center",
          }}
        >
          <MDTypography variant="h6" sx={{ color: ui.title }}>
            No matching assets found
          </MDTypography>
          <MDTypography variant="body2" sx={{ color: ui.muted, mt: 0.5 }}>
            Try changing the search text or status filter.
          </MDTypography>
        </Card>
      ) : (
        <TableContainer
          component={Card}
          sx={{
            boxShadow: "none",
            borderRadius: "22px",
            border: `1px solid ${ui.border}`,
          }}
        >
          <Table sx={{ overflowX: "auto", whiteSpace: "nowrap" }}>
            <TableBody>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Asset</TableCell>
                <TableCell>Serial Number</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Warranty</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableBody>

            <TableBody>
              {paginatedRecords.map((record) => (
                <TableRow
                  key={record.id}
                  hover
                  sx={{ cursor: "pointer" }}
                  onClick={() => openRowDetails(record)}
                >
                  <TableCell>{record.id}</TableCell>
                  <TableCell>{record.brand || "N/A"}</TableCell>
                  <TableCell>
                    <MDTypography
                      variant="button"
                      fontWeight="medium"
                      sx={{
                        maxWidth: "240px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        color: ui.title,
                      }}
                    >
                      {record.name || "Unnamed asset"}
                    </MDTypography>
                  </TableCell>
                  <TableCell>{record.serial_number || "N/A"}</TableCell>
                  <TableCell>
                    <Chip label={record.status} size="small" color={getStatusColor(record.status)} variant="outlined" />
                  </TableCell>
                  <TableCell>{formatDate(record.warranty_till)}</TableCell>
                  <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                    <Stack direction="row" spacing={1} justifyContent="center">
                      <IconButton size="small" onClick={() => handleDelete(record.id)}>
                        <Icon>
                          <Delete />
                        </Icon>
                      </IconButton>
                      <IconButton size="small" onClick={() => openEditDrawer(record)}>
                        <Icon>
                          <Edit />
                        </Icon>
                      </IconButton>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <TablePagination
            component="div"
            count={filteredRecords.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[10]}
            sx={{ display: "flex" }}
          />
        </TableContainer>
      )}
    </Card>
  );
};

export default Inventory;
